import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { API_ENDPOINTS } from "../config/apiConfig";
import { useAuth } from "../components/router/authContext";
import LongRunningTaskIndicator from "../components/general/LongRunningTaskIndicator";
import { buildAnalyticsCaseDetailPath } from "../apps/fleet_planning/analyticsViewMode";

const OptimizationRunContext = createContext(null);
const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled"]);
const OBJECTIVE_LABELS = {
  tco: "Minimize Cost",
  ev_adopt: "Accelerate EV Adoption",
  ghg: "Minimize Emissions",
};

const dismissedTerminalKey = (userId) => `fleet_queue_dismissed_terminal_${userId}`;
const runScopeStorageKey = (userId) => `fleet_run_scope_${userId}`;

const normalizeCount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? Math.round(numericValue) : null;
};

const formatObjectiveLabel = (objective) => OBJECTIVE_LABELS[String(objective || "").trim()] || "Fleet goal selected";

const buildRunScopeFromPayload = (payload) => {
  const selectedVehicles = Array.isArray(payload?.selected_vehicles) ? payload.selected_vehicles.length : 0;
  const selectedCustomVehicles = Array.isArray(payload?.selected_custom_user_vehicles)
    ? payload.selected_custom_user_vehicles.length
    : 0;

  return {
    routeCount: normalizeCount(Array.isArray(payload?.selected_routes) ? payload.selected_routes.length : null),
    vehicleCount: normalizeCount(selectedVehicles + selectedCustomVehicles),
    depotCount: normalizeCount(Array.isArray(payload?.selected_depots) ? payload.selected_depots.length : null),
    publicChargerCount: normalizeCount(
      Array.isArray(payload?.selected_charging_stations) ? payload.selected_charging_stations.length : null
    ),
    goalLabel: formatObjectiveLabel(payload?.objective),
  };
};

const buildScopeChips = (runScope) => {
  if (!runScope || typeof runScope !== "object") return [];
  const chips = [];
  if (runScope.routeCount !== null) chips.push(`${runScope.routeCount} routes`);
  if (runScope.vehicleCount !== null) chips.push(`${runScope.vehicleCount} vehicle options`);
  if (runScope.depotCount !== null) chips.push(`${runScope.depotCount} depots`);
  if (runScope.publicChargerCount !== null) chips.push(`${runScope.publicChargerCount} public chargers`);
  if (runScope.goalLabel) chips.push(`Goal: ${runScope.goalLabel}`);
  return chips;
};

const getRunPresentation = (run) => {
  const status = run?.status;
  if (status === "pending") {
    return {
      title: "Case queued",
      subtitle: "Your fleet plan is in line to run. It will start automatically.",
      reassurance: "You can leave this page and come back later from Case Library.",
    };
  }
  if (status === "running" && run?.solver_progress?.stage === "phase2") {
    return {
      title: "Refining the recommended plan",
      subtitle: "We've found promising options and are refining the recommendation.",
      reassurance: "You'll get a cost, charging, and emissions summary when this finishes.",
    };
  }
  return {
    title: "Evaluating fleet and charging options",
    subtitle: "We're comparing vehicle, route, depot, and charging combinations for this case.",
    reassurance: "This can take a few minutes...",
  };
};

const OptimizationRunPanel = ({ activeRun, queuedRuns, terminalRun, runScopeById, onCancelActive, onDismissTerminal }) => {
  const navigate = useNavigate();
  const queuedCount = Array.isArray(queuedRuns) ? queuedRuns.length : 0;
  const queuedRun = !activeRun && queuedCount > 0 ? queuedRuns[0] : null;
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const prevActiveRunIdRef = useRef(null);
  const prevQueuedCountRef = useRef(0);
  const prevTerminalRunIdRef = useRef(null);
  // Expanded panel should sit at the very bottom-right. Collapsed tab nudges upward slightly.
  const floatingPanelPositionClass = "fixed bottom-4 right-4 z-[1000]";
  const collapsedPanelPositionClass = "fixed bottom-20 right-0 z-[1000]";

  useEffect(() => {
    const activeRunId = activeRun?.id ?? null;
    const terminalRunId = terminalRun?.id ?? null;
    const queuedCountPrev = prevQueuedCountRef.current ?? 0;

    const queuedIncreased = queuedCount > queuedCountPrev;
    const activeChanged = Boolean(activeRunId) && activeRunId !== prevActiveRunIdRef.current;
    const terminalChanged = Boolean(terminalRunId) && terminalRunId !== prevTerminalRunIdRef.current;

    if (panelCollapsed && (queuedIncreased || activeChanged || terminalChanged)) {
      setPanelCollapsed(false);
    }

    prevActiveRunIdRef.current = activeRunId;
    prevQueuedCountRef.current = queuedCount;
    prevTerminalRunIdRef.current = terminalRunId;
  }, [activeRun?.id, queuedCount, panelCollapsed, terminalRun?.id]);

  if (!activeRun && !queuedRun && !terminalRun) {
    return null;
  }

  if (panelCollapsed) {
    return (
      <div className={collapsedPanelPositionClass}>
        <button
          type="button"
          onClick={() => setPanelCollapsed(false)}
          aria-label="Expand optimization panel"
          className="flex h-11 items-center gap-2 rounded-l-xl border border-r-0 border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 shadow-xl hover:bg-slate-50"
        >
          <FaChevronLeft className="text-sm" />
          <span>Running Panel</span>
        </button>
      </div>
    );
  }

  if (activeRun || queuedRun) {
    const displayedRun = activeRun || queuedRun;
    const runPresentation = getRunPresentation(displayedRun);
    const fallbackScope = { goalLabel: formatObjectiveLabel(displayedRun?.objective) };
    const scopeChips = buildScopeChips(runScopeById?.[displayedRun?.id] || fallbackScope);
    const metaChips = [];
    if (displayedRun?.case_name) {
      metaChips.push(`Case: ${displayedRun.case_name}`);
    }
    if (!activeRun && displayedRun?.queue_position) {
      metaChips.push(`Queue position: ${displayedRun.queue_position}`);
    }
    const queueSummary = activeRun
      ? queuedCount > 0
        ? `${queuedCount} case${queuedCount > 1 ? "s" : ""} queued behind this run.`
        : ""
      : queuedCount > 1
        ? `${queuedCount - 1} additional case${queuedCount - 1 > 1 ? "s are" : " is"} waiting in queue.`
        : "";

    return (
      <div className={`${floatingPanelPositionClass} w-[26rem]`}>
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={() => setPanelCollapsed(true)}
            aria-label="Collapse optimization panel"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 hover:bg-slate-50"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
        <LongRunningTaskIndicator
          visible={true}
          title={runPresentation.title}
          phase={runPresentation.subtitle}
          reassurance={runPresentation.reassurance}
          scopeChips={scopeChips}
          metaChips={metaChips}
          elapsedSeconds={
            displayedRun?.started_at
              ? Math.max(0, Math.floor((Date.now() - new Date(displayedRun.started_at).getTime()) / 1000))
              : 0
          }
          startedAt={displayedRun?.started_at || null}
          onCancel={activeRun ? onCancelActive : null}
          requestId={displayedRun?.request_id}
          solverProgress={displayedRun?.solver_progress || null}
        />
        {queueSummary && (
          <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm">
            {queueSummary}
            <button
              type="button"
              onClick={() => navigate("/fleet/analytics")}
              className="ml-2 font-semibold text-blue-600 hover:text-blue-700"
            >
              Open queue
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!terminalRun) {
    return null;
  }

  const titleByStatus = {
    completed: "Recommendation ready",
    failed: "Run needs attention",
    cancelled: "Run cancelled",
  };
  const bodyByStatus = {
    completed: terminalRun.result_case_name
      ? `Case "${terminalRun.result_case_name}" is ready to review.`
      : "Your fleet plan is ready to review.",
    failed: terminalRun.error || "We could not finish this case. Review the setup and try again.",
    cancelled: terminalRun.error || "This case was stopped before results were generated.",
  };

  return (
    <div className={`${floatingPanelPositionClass} w-[26rem]`}>
      <div className="mb-2 flex justify-end">
        <button
          type="button"
          onClick={() => setPanelCollapsed(true)}
          aria-label="Collapse optimization panel"
          className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 hover:bg-slate-50"
        >
          <FaChevronRight className="text-xs" />
        </button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5 font-ui shadow-2xl">
        <div className="text-base font-bold text-slate-900">{titleByStatus[terminalRun.status] || "Optimization status"}</div>
        <div className="mt-2 text-sm text-slate-700">{bodyByStatus[terminalRun.status] || ""}</div>
        <div className="mt-4 flex items-center justify-end gap-2">
          {terminalRun.status === "completed" && terminalRun.result_case_id && (
            <button
              type="button"
              onClick={() => navigate(buildAnalyticsCaseDetailPath(terminalRun.result_case_id))}
              className="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Open results
            </button>
          )}
          <button
            type="button"
            onClick={onDismissTerminal}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export const OptimizationRunProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const authToken = localStorage.getItem("authToken");
  const pollRef = useRef(null);
  const prevActiveRunIdRef = useRef(null);
  const [activeRun, setActiveRun] = useState(null);
  const [queuedRuns, setQueuedRuns] = useState([]);
  const [terminalRun, setTerminalRun] = useState(null);
  const [runScopeById, setRunScopeById] = useState({});

  const headers = useMemo(() => (authToken ? { Authorization: `Token ${authToken}` } : null), [authToken]);

  const applyQueueSnapshot = useCallback(
    (snapshot) => {
      const nextActive = snapshot?.active_run || null;
      const nextQueued = Array.isArray(snapshot?.queued_runs) ? snapshot.queued_runs : [];
      setActiveRun(nextActive);
      setQueuedRuns(nextQueued);
      return { nextActive, nextQueued };
    },
    []
  );

  const persistRunScopeMap = useCallback(
    (nextMap) => {
      if (!currentUser?.id) return;
      try {
        sessionStorage.setItem(runScopeStorageKey(currentUser.id), JSON.stringify(nextMap));
      } catch (_) {
        // Ignore storage quota or serialization errors.
      }
    },
    [currentUser?.id]
  );

  const registerRunScope = useCallback(
    (runId, payload) => {
      const normalizedRunId = String(runId || "").trim();
      if (!normalizedRunId) return;
      const nextScope = buildRunScopeFromPayload(payload);
      setRunScopeById((previousMap) => {
        const updatedMap = {
          ...previousMap,
          [normalizedRunId]: nextScope,
        };
        persistRunScopeMap(updatedMap);
        return updatedMap;
      });
    },
    [persistRunScopeMap]
  );

  const fetchQueue = useCallback(async () => {
    if (!headers || !currentUser?.id) return { active_run: null, queued_runs: [] };
    const response = await axios.get(API_ENDPOINTS.optimizeRunsQueue, { headers });
    return response?.data || { active_run: null, queued_runs: [] };
  }, [headers, currentUser?.id]);

  const fetchRunStatus = useCallback(
    async (runId) => {
      if (!headers || !currentUser?.id || !runId) return null;
      const response = await axios.get(API_ENDPOINTS.optimizeRunsStatus, {
        headers,
        params: { run_id: runId },
      });
      return response?.data || null;
    },
    [headers, currentUser?.id]
  );

  const refreshQueue = useCallback(async () => {
    const snapshot = await fetchQueue();
    const previousActiveRunId = prevActiveRunIdRef.current;
    const { nextActive } = applyQueueSnapshot(snapshot);
    const nextActiveRunId = nextActive?.id || null;
    if (previousActiveRunId && previousActiveRunId !== nextActiveRunId) {
      try {
        const finished = await fetchRunStatus(previousActiveRunId);
        if (finished && TERMINAL_STATUSES.has(finished.status)) {
          const dismissedId = sessionStorage.getItem(dismissedTerminalKey(currentUser.id));
          if (!dismissedId || String(dismissedId) !== String(finished.id)) {
            setTerminalRun(finished);
          }
        }
      } catch (_) {
        // Ignore terminal fetch errors.
      }
    }
    prevActiveRunIdRef.current = nextActiveRunId;
    return snapshot;
  }, [applyQueueSnapshot, currentUser?.id, fetchQueue, fetchRunStatus]);

  useEffect(() => {
    if (!headers || !currentUser?.id) {
      setActiveRun(null);
      setQueuedRuns([]);
      setTerminalRun(null);
      setRunScopeById({});
      prevActiveRunIdRef.current = null;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    let mounted = true;
    const hydrate = async () => {
      try {
        setRunScopeById({});
        const storedScopes = sessionStorage.getItem(runScopeStorageKey(currentUser.id));
        if (storedScopes) {
          const parsedScopes = JSON.parse(storedScopes);
          if (parsedScopes && typeof parsedScopes === "object") {
            setRunScopeById(parsedScopes);
          }
        }
        await refreshQueue();
      } catch (_) {
        if (!mounted) return;
        setActiveRun(null);
        setQueuedRuns([]);
      }
    };
    hydrate();
    return () => {
      mounted = false;
    };
  }, [currentUser?.id, headers, refreshQueue]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!headers || !currentUser?.id || (!activeRun && queuedRuns.length === 0)) {
      return undefined;
    }
    pollRef.current = setInterval(() => {
      refreshQueue().catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [activeRun, queuedRuns.length, currentUser?.id, headers, refreshQueue]);

  const startRun = useCallback(
    async (payload) => {
      if (!headers || !currentUser?.id) {
        throw new Error("You must be logged in to start optimization.");
      }
      sessionStorage.removeItem(dismissedTerminalKey(currentUser.id));
      const response = await axios.post(
        API_ENDPOINTS.optimizeRunsCreate,
        {
          ...payload,
          origin_path: location.pathname,
        },
        { headers }
      );
      const created = response?.data || null;
      if (created?.id) {
        registerRunScope(created.id, payload);
      }
      if (created?.queue) {
        applyQueueSnapshot(created.queue);
        prevActiveRunIdRef.current = created.queue?.active_run?.id || null;
      } else {
        await refreshQueue();
      }
      return created;
    },
    [applyQueueSnapshot, currentUser?.id, headers, location.pathname, refreshQueue, registerRunScope]
  );

  const cancelRun = useCallback(
    async (runId = null) => {
      const targetRunId = runId || activeRun?.id;
      if (!headers || !targetRunId) return null;
      const response = await axios.post(
        API_ENDPOINTS.optimizeRunsCancel,
        { run_id: targetRunId },
        { headers }
      );
      await refreshQueue();
      return response?.data || null;
    },
    [activeRun?.id, headers, refreshQueue]
  );

  const reorderQueuedRun = useCallback(
    async (runId, direction) => {
      if (!headers || !runId) return null;
      const response = await axios.post(
        API_ENDPOINTS.optimizeRunsReorder,
        { run_id: runId, direction },
        { headers }
      );
      const queue = response?.data?.queue;
      if (queue) {
        applyQueueSnapshot(queue);
        prevActiveRunIdRef.current = queue?.active_run?.id || null;
      } else {
        await refreshQueue();
      }
      return response?.data || null;
    },
    [applyQueueSnapshot, headers, refreshQueue]
  );

  const removeQueuedRun = useCallback(
    async (runId) => {
      if (!headers || !runId) return null;
      const response = await axios.post(
        API_ENDPOINTS.optimizeRunsRemove,
        { run_id: runId },
        { headers }
      );
      const queue = response?.data?.queue;
      if (queue) {
        applyQueueSnapshot(queue);
        prevActiveRunIdRef.current = queue?.active_run?.id || null;
      } else {
        await refreshQueue();
      }
      return response?.data || null;
    },
    [applyQueueSnapshot, headers, refreshQueue]
  );

  const dismissTerminalRun = useCallback(() => {
    if (currentUser?.id && terminalRun?.id) {
      sessionStorage.setItem(dismissedTerminalKey(currentUser.id), String(terminalRun.id));
    }
    setTerminalRun(null);
  }, [currentUser?.id, terminalRun?.id]);

  const contextValue = useMemo(
    () => ({
      activeRun,
      queuedRuns,
      run: activeRun,
      isRunActive: Boolean(activeRun),
      queueCount: queuedRuns.length,
      startRun,
      cancelRun,
      reorderQueuedRun,
      removeQueuedRun,
      refreshQueue,
    }),
    [activeRun, cancelRun, queuedRuns, refreshQueue, reorderQueuedRun, removeQueuedRun, startRun]
  );

  return (
    <OptimizationRunContext.Provider value={contextValue}>
      {children}
      <OptimizationRunPanel
        activeRun={activeRun}
        queuedRuns={queuedRuns}
        terminalRun={terminalRun}
        runScopeById={runScopeById}
        onCancelActive={() => cancelRun(activeRun?.id)}
        onDismissTerminal={dismissTerminalRun}
      />
    </OptimizationRunContext.Provider>
  );
};

export const useOptimizationRun = () => {
  const context = useContext(OptimizationRunContext);
  if (!context) {
    throw new Error("useOptimizationRun must be used within an OptimizationRunProvider");
  }
  return context;
};
